import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAdmin } from "@/integrations/supabase/role-middleware";
import {
  publishedSiteEventEndCutoff,
  siteDateKeyFromInstant,
  toUtcIso,
} from "@/lib/datetime.schedule";

const siteEventFields =
  "id, date_label, start_date, start_at, end_at, capacity, title, audience, details, published, created_at, updated_at";

export const listPublishedEvents = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const cutoff = toUtcIso(publishedSiteEventEndCutoff());
  const { data, error } = await supabaseAdmin
    .from("site_events")
    .select(siteEventFields)
    .eq("published", true)
    .gt("end_at", cutoff)
    .order("start_at", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const listAllEvents = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const { data, error } = await supabase
      .from("site_events")
      .select(siteEventFields)
      .order("start_at", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

const siteEventInput = z.object({
  date_label: z.string().min(1).max(200),
  start_at: z.string().datetime(),
  end_at: z.string().datetime(),
  capacity: z.number().int().positive(),
  title: z.string().min(1).max(200),
  audience: z.string().min(1).max(200),
  details: z.string().min(1).max(2000),
  published: z.boolean().optional(),
});

function deriveStartDate(startAt: string): string {
  return siteDateKeyFromInstant(new Date(startAt));
}

function validateEventTimes(startAt: string, endAt: string) {
  if (new Date(endAt).getTime() <= new Date(startAt).getTime()) {
    throw new Error("End datetime must be after start datetime.");
  }
}

export const createEvent = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => siteEventInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    validateEventTimes(data.start_at, data.end_at);

    const { data: event, error } = await supabase
      .from("site_events")
      .insert({
        date_label: data.date_label.trim(),
        start_date: deriveStartDate(data.start_at),
        start_at: data.start_at,
        end_at: data.end_at,
        capacity: data.capacity,
        title: data.title.trim(),
        audience: data.audience.trim(),
        details: data.details.trim(),
        published: data.published ?? true,
      })
      .select(siteEventFields)
      .single();
    if (error) throw new Error(error.message);
    return event;
  });

export const updateEvent = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) =>
    siteEventInput
      .extend({
        id: z.string().uuid(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    validateEventTimes(data.start_at, data.end_at);

    const { data: event, error } = await supabase
      .from("site_events")
      .update({
        date_label: data.date_label.trim(),
        start_date: deriveStartDate(data.start_at),
        start_at: data.start_at,
        end_at: data.end_at,
        capacity: data.capacity,
        title: data.title.trim(),
        audience: data.audience.trim(),
        details: data.details.trim(),
        published: data.published ?? true,
      })
      .eq("id", data.id)
      .select(siteEventFields)
      .single();
    if (error) throw new Error(error.message);
    return event;
  });

export const deleteEvent = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { error } = await supabase.from("site_events").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
